<template>
  <Viewport
    :document-id="documentId"
    class="m-pdf-viewer__viewport"
    :class="{ 'm-pdf-viewer__viewport--no-scroll': singlePage }"
    @pointerdown.capture="swallowNonPrimary"
    @mousedown.capture="swallowNonPrimary"
    @contextmenu="handleContextMenu"
  >
    <GlobalPointerProvider>
      <Scroller :document-id="documentId">
        <template #default="{ page }">
          <PagePointerProvider :document-id="documentId" :page-index="page.pageIndex">
            <RenderLayer :document-id="documentId" :page-index="page.pageIndex" />
            <SelectionLayer
              v-if="selectable"
              :document-id="documentId"
              :page-index="page.pageIndex"
            />
          </PagePointerProvider>
        </template>
      </Scroller>
    </GlobalPointerProvider>
  </Viewport>
  <Teleport to="body">
    <div
      v-if="contextMenu"
      class="m-pdf-viewer__context-menu"
      :style="{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }"
      role="menu"
    >
      <button type="button" role="menuitem" @click="copyAndClose">Copy</button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Viewport } from "@embedpdf/plugin-viewport/vue";
import { Scroller } from "@embedpdf/plugin-scroll/vue";
import { RenderLayer } from "@embedpdf/plugin-render/vue";
import { SelectionLayer, useSelectionCapability } from "@embedpdf/plugin-selection/vue";
import {
  GlobalPointerProvider,
  PagePointerProvider,
} from "@embedpdf/plugin-interaction-manager/vue";
import { useDocumentManagerCapability } from "@embedpdf/plugin-document-manager/vue";

const props = withDefaults(
  defineProps<{
    documentId: string;
    url: string;
    singlePage?: boolean;
    selectable?: boolean;
  }>(),
  { selectable: true },
);

const { provides: docManagerCap } = useDocumentManagerCapability();
const { provides: selectionCap } = useSelectionCapability();

// The selection plugin only runs its text-selection logic for interaction
// modes that have opted in. The default mode is `pointerMode`, which is
// otherwise selection-free — enable text selection on it as soon as the
// capability resolves and re-enable on each doc swap.
watch(
  [selectionCap, () => props.documentId, () => props.selectable],
  ([sel, docId, on]) => {
    if (!sel || !docId) return;
    sel.enableForMode("pointerMode", { enableSelection: !!on, showSelectionRects: !!on }, docId);
  },
  { immediate: true },
);

// The selection plugin exposes copyToClipboard() but doesn't listen for
// the native Cmd/Ctrl+C event itself — wire it here. The shipped
// CopyToClipboard helper component then forwards the resulting event to
// navigator.clipboard.writeText(text).
function handleCopy(e: ClipboardEvent) {
  const sel = selectionCap.value;
  if (!sel || !props.selectable) return;
  const state = sel.getState(props.documentId);
  if (!state?.selection) return;
  e.preventDefault();
  sel.copyToClipboard(props.documentId);
}
onMounted(() => document.addEventListener("copy", handleCopy));
onBeforeUnmount(() => document.removeEventListener("copy", handleCopy));

// EmbedPDF's selection plugin clears the selection on every pointerdown
// without filtering by button — right-click would deselect. Swallow
// non-primary-button pointer/mousedowns at capture phase before the
// plugin sees them. The plugin maps both `pointerdown` AND `mousedown`
// to the same onPointerDown handler so we have to block both.

function swallowNonPrimary(e: MouseEvent) {
  if (e.button !== 0) {
    e.stopPropagation();
    e.stopImmediatePropagation();
  }
}

// PDF selection isn't a DOM selection, so the browser's native context
// menu has nothing to offer for it. Render our own "Copy" menu at the
// cursor when the user right-clicks with a live selection.
const contextMenu = ref<{ x: number; y: number } | null>(null);

function handleContextMenu(e: MouseEvent) {
  const sel = selectionCap.value;
  if (!sel || !props.selectable) return;
  const state = sel.getState(props.documentId);
  if (!state?.selection) return;
  e.preventDefault();
  contextMenu.value = { x: e.clientX, y: e.clientY };
}

function closeContextMenu() {
  contextMenu.value = null;
}

// Don't close on clicks inside the menu — otherwise the menu unmounts on
// the Copy button's own pointerdown, before its @click ever fires.
function handleOutsidePointerDown(e: PointerEvent) {
  const target = e.target as Element | null;
  if (target?.closest(".m-pdf-viewer__context-menu")) return;
  closeContextMenu();
}

function copyAndClose() {
  selectionCap.value?.copyToClipboard(props.documentId);
  closeContextMenu();
}

onMounted(() => {
  document.addEventListener("pointerdown", handleOutsidePointerDown);
  document.addEventListener("scroll", closeContextMenu, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", handleOutsidePointerDown);
  document.removeEventListener("scroll", closeContextMenu, true);
});

// Swap the active document in place when the URL prop changes (instead
// of remounting EmbedPDF). Lets the viewer keep its scroll position +
// search state when preview-form re-fetches the PDF on form updates.
let initialUrl = props.url;
watch(
  () => props.url,
  async (newUrl) => {
    if (!newUrl || newUrl === initialUrl) return;
    initialUrl = newUrl;
    const dm = docManagerCap.value;
    if (!dm) return;
    const currentId = dm.getActiveDocumentId();

    // Snapshot scrollTop on the DOM container so we can restore it after
    // the doc swap — the new doc's layout reset would otherwise put us
    // back at 0.
    const viewportEl = document.querySelector<HTMLElement>(
      props.singlePage
        ? ".m-pdf-viewer__viewport--no-scroll"
        : ".m-pdf-viewer__viewport:not(.m-pdf-viewer__viewport--no-scroll)",
    );
    const savedScrollTop = viewportEl?.scrollTop ?? 0;

    try {
      await dm.openDocumentUrl({ url: newUrl, autoActivate: true }).toPromise();
      if (currentId) await dm.closeDocument(currentId).toPromise();
      // The new doc's RenderLayer images are sized and slotted by the
      // Scroller asynchronously — restore on a couple of animation frames
      // to outlast any layout that would otherwise reset to 0.
      if (viewportEl) {
        const restore = () => {
          viewportEl.scrollTop = savedScrollTop;
        };
        requestAnimationFrame(() => {
          restore();
          requestAnimationFrame(restore);
          setTimeout(restore, 80);
        });
      }
    } catch (e) {
      console.warn("[MPdfViewerDocument] swap doc failed", e);
    }
  },
);
</script>

<style>
/* unscoped — context menu teleports to <body> outside the component tree */
.m-pdf-viewer__context-menu {
  position: fixed;
  z-index: 10000;
  background: #fff;
  border: 1px solid var(--border-colour, #d0d0d0);
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  padding: 4px;
  min-width: 140px;
}

.m-pdf-viewer__context-menu button {
  display: block;
  width: 100%;
  padding: 6px 12px;
  background: transparent;
  border: 0;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border-radius: 4px;
}

.m-pdf-viewer__context-menu button:hover,
.m-pdf-viewer__context-menu button:focus {
  background: var(--grey-lightest, #f2f3f5);
  outline: none;
}
</style>

<style scoped>
.m-pdf-viewer__viewport {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #fff;
}

.m-pdf-viewer__viewport--no-scroll {
  background: transparent;
}

.m-pdf-viewer__viewport--no-scroll::-webkit-scrollbar {
  display: none;
}

/* The PDF page image must not capture the drag gesture — otherwise the
   browser starts a native image-drag and the SelectionLayer never sees
   the pointer events, so text selection never engages. */
.m-pdf-viewer__viewport :deep(img) {
  user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  pointer-events: none;
}
</style>
